import {
  isPresetRange,
  type TimeRangeMode,
} from "@/shared/domain/dashboards/DashboardDefinition";
import { TimeRange } from "@/shared/domain/dashboards/TimeRange";
import type { Period } from "@/shared/domain/metrics";

import type { BucketPlaceholder } from "./fillMissingBuckets";
import { advancePeriodNs, truncateToPeriodNs } from "./periodBounds";

const NS_PER_MS = 1_000_000n;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** The floor an All-time axis is padded out to. The preset itself resolves to
 *  ten years, which draws a decade of emptiness around a month of data; a card
 *  whose own data reaches further back widens past this on its own. */
const ALL_TIME_FLOOR_DAYS = 365;

/**
 * The window every card on one dashboard reads and draws, in Unix-epoch
 * nanoseconds, plus the period its bounds are snapped to.
 *
 * A FLOOR for the axis, not a clamp: {@link padAxisToDomain} only ever adds
 * slots, so data outside these bounds keeps its own.
 *
 * Both bounds sit on whole-period boundaries, and `period` says which period's.
 * That is what lets an axis be walked outward in calendar steps rather than in
 * measured bucket widths — see {@link padAxisToDomain} — and what lets the raw
 * and aggregated paths agree, since they can now be handed one window rather
 * than each deriving its own.
 */
export interface AxisDomain {
  startNs: bigint;
  endNs: bigint;
  /** The period `startNs`/`endNs` are snapped to, and the stride padding
   *  steps by. */
  period: Period;
}

/**
 * The window every chart card on one dashboard reads and pads its axis out to,
 * snapped to the calendar boundaries the server buckets on.
 *
 * A bounded preset or a custom range IS the window, so every card on the
 * dashboard draws the same span whatever its own data covers — which is the
 * whole point: two cards whose metrics stop reporting in different weeks used
 * to end up with visibly different axes.
 *
 * The bounds are then SNAPPED, floor and ceiling, exactly as `aggregate_metric`
 * snaps its own window before grouping (`window_start`/`window_end` in
 * `metrics_repo.py`): the start floors to its period's boundary, and the end
 * rises to the next one unless it already sits on one. Snapped rather than
 * literal because the snapped window is the one the server actually reads —
 * anchoring to the raw selection would describe a span no response covers, and
 * would leave a raw card pinned to it drawing a different window from the
 * aggregated card beside it.
 *
 * "All time" is the exception to the window being the selection. It resolves to
 * ten years, and anchoring to that would draw a decade around whatever the data
 * actually covers, so it floors at one year (snapped like any other bound) and
 * lets each card's data widen it. Cards therefore agree exactly while their
 * data sits inside the last year, and a card holding older data reaches further
 * back than its neighbours — the accepted cost of keeping this computable from
 * one card's own response, with no cross-card coordination and no axis that
 * shifts as sibling cards resolve.
 */
export function resolveAxisDomain(
  range: TimeRange,
  mode: TimeRangeMode,
  period: Period,
): AxisDomain {
  const rawStartMs =
    isPresetRange(mode) && mode === "all"
      ? range.end_ms - ALL_TIME_FLOOR_DAYS * MS_PER_DAY
      : range.start_ms;
  const rawEndNs = BigInt(range.end_ms) * NS_PER_MS;
  const flooredEndNs = truncateToPeriodNs(rawEndNs, period);
  return {
    startNs: truncateToPeriodNs(BigInt(rawStartMs) * NS_PER_MS, period),
    // An end already on a boundary is the window it asked for; anything else
    // widens to the end of the period holding it, so the bucket that period
    // produces has a slot to sit in. Mirrors the backend's `window_end` CASE.
    endNs:
      rawEndNs === flooredEndNs
        ? rawEndNs
        : advancePeriodNs(flooredEndNs, period),
    period,
  };
}

/** Ceiling on synthesized buckets, shared by both directions, so a
 *  mis-resolved domain degrades to a short axis rather than locking the tab
 *  up. One year of daily buckets is 365, the widest an honest domain reaches. */
const MAX_PADDED_BUCKETS = 1000;

/**
 * Extend a gap-filled axis outward until it spans `domain`.
 *
 * Steps in `domain.period`'s CALENDAR units, not in the measured width of the
 * axis's end buckets. The two agree for daily and weekly, whose buckets are a
 * fixed span, and disagree for every other period: a February-wide step lands
 * the next monthly slot on March 29 and each one after it further adrift, while
 * the server's own buckets sit on the first of each month. Stepping on the
 * calendar puts a synthesized slot exactly where `date_trunc` would have put a
 * real one.
 *
 * Only ever ADDS slots. An axis reaching past the domain keeps its own bounds,
 * which is what lets a card holding data older than an All-time floor draw it.
 *
 * An empty axis comes back empty: a card with no data draws its empty state
 * anyway, and synthesizing a domain's worth of placeholders for it would
 * replace that message with a chart of nothing.
 *
 * The budget is spent forward first. A domain's trailing edge is "now" on every
 * preset, so the forward run is short and the backward one — the side that can
 * be asked for a decade — takes what is left.
 */
export function padAxisToDomain<T extends BucketPlaceholder>(
  axis: T[],
  domain: AxisDomain,
): (T | BucketPlaceholder)[] {
  const first = axis[0];
  const last = axis[axis.length - 1];
  if (first === undefined || last === undefined) {
    return axis;
  }

  let budget = MAX_PADDED_BUCKETS;

  const trailing: BucketPlaceholder[] = [];
  let end = last.end_time;
  while (end < domain.endNs && budget > 0) {
    const next = advancePeriodNs(end, domain.period);
    // A period that failed to advance would spin here forever. Unreachable for
    // a well-formed period, but this loop is bounded on time rather than on a
    // count computed up front, so the degenerate case is worth naming.
    if (next <= end) {
      break;
    }
    trailing.push({ start_time: end, end_time: next });
    end = next;
    budget -= 1;
  }

  const leading: BucketPlaceholder[] = [];
  let start = first.start_time;
  while (start > domain.startNs && budget > 0) {
    const previous = advancePeriodNs(start, domain.period, -1);
    if (previous >= start) {
      break;
    }
    leading.unshift({ start_time: previous, end_time: start });
    start = previous;
    budget -= 1;
  }

  return [...leading, ...axis, ...trailing];
}
